import React from 'react';
import clsx from 'clsx';
import { Dropdown, Button } from '@insurancemenu/imenu-components-lib';
import { DropdownOptionType, OptionsAlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types/Dropdown';
import {
  ChevronDown,
  CloudUpload,
  Delete,
  Mail,
  Edit,
  UserGroup,
  Download,
} from '@insurancemenu/imenu-components-lib/dist/icons';
import { useRootStore } from 'store/rootStore';
import { InstalledEnrollmentType } from 'types/enrollmentService';
import { BiGroup } from 'types/groupService';
import { getUploadExhibitZipUrl } from 'api/operations/carrierFileService';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { createStyles } from './styles';

export interface MoreDropdownProps {
  isManageEmailsAvailable: boolean;
  period: InstalledEnrollmentType;
  group: BiGroup;
  groupPeriods: InstalledEnrollmentType[];
  openEmailsModal: () => void;
}
const MoreDropdown = ({
  isManageEmailsAvailable,
  period,
  group,
  groupPeriods,
  openEmailsModal,
}: MoreDropdownProps): JSX.Element => {
  const classes = createStyles();

  const {
    triggerRenewalsStore: {
      openWarningModal,
      setCurrentPeriod,
      setCurrentGroup,
      setUploadModalStatus,
      setIsFileUploadReset,
      updateAllGroupPeriods,
      viewGroupProfile,
    },
  } = useRootStore();

  const updateCurrentInfo = (): void => {
    setCurrentPeriod(period);
    setCurrentGroup(group);
  };
  const onViewGroupProfile = async (): Promise<void> => {
    await viewGroupProfile(group.id, group.agency.id, group.agency.name);
  };

  // Collect all exhibitUrls from all product lines in the period
  const allExhibitUrls =
    period.installedProposal?.productLines
      .flatMap((productLine) => productLine.exhibitUrls || [])
      .filter((url) => url) || [];

  const downloadExhibit = async (): Promise<void> => {
    if (allExhibitUrls.length > 0) {
      const zipUrl = await getUploadExhibitZipUrl(allExhibitUrls);
      if (zipUrl) {
        downloadByUrl(zipUrl);
      }
    }
  };

  const dropdownOptions: DropdownOptionType[] = [
    {
      value: 'viewGroupProfile',
      icon: UserGroup,
      label: 'View Group Profile',
      onOptionClick: onViewGroupProfile,
    },
    {
      value: 'manageEmails',
      icon: Mail,
      label: 'Manage Emails',
      disabled: !isManageEmailsAvailable,
      onOptionClick: isManageEmailsAvailable
        ? (): void => {
            updateCurrentInfo();
            openEmailsModal();
          }
        : undefined,
    },
    {
      value: 'uploadExhibit',
      icon: CloudUpload,
      label: 'Upload Exhibit',
      onOptionClick: (): void => {
        updateCurrentInfo();
        setUploadModalStatus(true);
        setIsFileUploadReset(false);
      },
    },
    {
      value: 'downloadExhibits',
      icon: Download,
      label: 'Download Exhibits',
      disabled: allExhibitUrls.length === 0,
      onOptionClick: allExhibitUrls.length > 0 ? downloadExhibit : undefined,
    },
    {
      value: 'editEffectiveDate',
      icon: Edit,
      label: 'Change Effective Date',
      onOptionClick: (): void => {
        updateCurrentInfo();
        updateAllGroupPeriods(groupPeriods);
        openWarningModal('CHANGE_EFFECTIVE_DATE');
      },
    },
    {
      value: 'removePeriod',
      icon: Delete,
      label: 'Remove Period',
      customColor: 'red',
      onOptionClick: (): void => {
        updateCurrentInfo();
        openWarningModal('REMOVE_PERIOD');
      },
    },
  ];

  return (
    <Dropdown
      name="More"
      options={dropdownOptions}
      optionsAlignment={OptionsAlignmentTypes.BOTTOM_LEFT}
      optionsWidth="22rem"
      closedElement={
        <Button
          className={clsx(classes.dropdownOpenedBtn, classes.dropdownButton)}
          color="primary"
          type="text"
          rightIconProp={<ChevronDown />}
        >
          MORE
        </Button>
      }
      openedElement={
        <Button color="primary" className={classes.dropdownButton} rightIconProp={<ChevronDown />}>
          More
        </Button>
      }
    />
  );
};

export default MoreDropdown;
